import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { eslTourStats } from '../api/tournaments';
import styles from '../static/styles/TournamentDetails.module.css';
import { useNavigate } from 'react-router-dom';

import ArrowLeft from '../static/img/backArrow.png'
import headerBg from '../static/img/WeeklyTournaments/tournamentsHeader.svg';
import entryBg from '../static/img/WeeklyTournaments/tournamentEntryBG.svg';
import winnerIcon from '../static/img/WeeklyTournaments/winnerTickIcon.svg';
import loserIcon from '../static/img/WeeklyTournaments/loserCrossIcon.svg';
import defaultAvatar from '../static/img/Sidebar/Avatar.png';
import winnerMedal from '../static/img/MadeByESL/tour_winner_medal.svg';

const TournamentDetails = () => {
  const { tournamentId } = useParams();
  const [tournamentData, setTournamentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeRound, setActiveRound] = useState(0);
  const tabsRef = useRef(null);
  const activeTabRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTournamentData = async () => {
      if (!tournamentId) {
        setError('Tournament ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await eslTourStats(tournamentId);

        if (response && response.rounds) {
          setTournamentData(response);
          // Default to the latest round
          const sorted = [...response.rounds].sort((a, b) => a.round_number - b.round_number);
          setActiveRound(sorted.length - 1);
        } else {
          setError('No tournament data available');
        }
      } catch (err) {
        setError('Failed to load tournament data');
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentData();
  }, [tournamentId]);

  useEffect(() => {
    document.title = "Tournament Details | Jazz ESL"
  })

  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [activeRound]);

  const getSortedRounds = () => {
    if (!tournamentData?.rounds) return [];
    return [...tournamentData.rounds].sort((a, b) => a.round_number - b.round_number);
  };

  const sortedRounds = getSortedRounds();
  const currentRound = sortedRounds[activeRound];

  const getRoundWinner = (round) => {
    if (!round) return null;
    if (round.matches.length !== 1) return null;
    const match = round.matches[0];
    if (match.status === 'upcoming') return null;
    const allEliminated = match.players?.every(p => p.has_appeared === false);
    if (allEliminated) return null;
    return match.result?.winner || null;
  };

  const isMatchAllEliminated = (match) => {
    if (!match) return false;
    return match.status === 'completed' && match.result?.winner === null;
  };

  const handlePrevRound = () => {
    if (activeRound > 0) setActiveRound(activeRound - 1);
  };

  const handleNextRound = () => {
    if (activeRound < sortedRounds.length - 1) setActiveRound(activeRound + 1);
  };

  const truncateName = (name, max = 8) => {
    if (!name) return '???';
    return name.length > max ? `${name.substring(0, max)}..` : name;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading tournament details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!tournamentData || !tournamentData.rounds) {
    return (
      <div className={styles.container}>
        <div className={styles.noData}>No tournament data available</div>
      </div>
    );
  }

  const roundWinner = getRoundWinner(currentRound);
  const isFinalRound = activeRound === sortedRounds.length - 1;
  const finalMatch = isFinalRound && currentRound?.matches?.length === 1 ? currentRound.matches[0] : null;
  const otherMatches = finalMatch ? [] : (currentRound?.matches || []).slice().sort((a, b) => {
    const aIsBye = a.players.length === 1;
    const bIsBye = b.players.length === 1;
    if (aIsBye && !bIsBye) return -1; // bye goes to top
    if (!aIsBye && bIsBye) return 1;
    const aEliminated = isMatchAllEliminated(a);
    const bEliminated = isMatchAllEliminated(b);
    if (aEliminated && !bEliminated) return 1;
    if (!aEliminated && bEliminated) return -1;
    return 0;
  });

  const totalMatches = tournamentData?.rounds?.reduce(
    (sum, round) => sum + (round?.round_matches || 0),
    0
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.navigationHeader}>
        <div
          className={styles.navigationBack}
          onClick={() => navigate(-1)}
        >
          <img src={ArrowLeft} alt="<" />
        </div>
        <div className={styles.navigationTitle}>
          <p>Tournaments</p>
          <h2 className={styles.heading}>Leaderboard</h2>
        </div>
      </div>
      <div className={styles.gamingTaskHeader}>
        <p className={styles.subheading}>Tournament</p>
        <h2 className={styles.heading}>Leaderboard</h2>
        <div className={styles.headerstatsContainer}>
          <div className={styles.headerstats}>
            <span>{tournamentData.rounds.length}</span>
            <p>Rounds</p>
          </div>
          <div className={styles.headerstats}>
            <span>{totalMatches}</span>
            <p>Matches</p>
          </div>
          <div className={styles.headerstats}>
            <span>{totalMatches * 2}</span>
            <p>Players</p>
          </div>
        </div>
      </div>

      {/* Round Tabs */}
      <div className={styles.roundTabs}>
        <button className={styles.roundArrow} onClick={handlePrevRound} disabled={activeRound === 0}>
          &laquo;
        </button>
        <div className={styles.roundTabsScroll} ref={tabsRef}>
          {sortedRounds.map((round, index) => (
            <button
              key={round.round_number}
              ref={index === activeRound ? activeTabRef : null}
              className={`${styles.roundTab} ${index === activeRound ? styles.roundTabActive : ''}`}
              onClick={() => setActiveRound(index)}
            >
              {round.title || `Round ${round.round_number}`}
            </button>
          ))}
        </div>
        <button className={styles.roundArrow} onClick={handleNextRound} disabled={activeRound === sortedRounds.length - 1}>
          &raquo;
        </button>
      </div>

      {/* Round Winner */}
      {roundWinner && (
        <div className={styles.roundWinnerBanner}>
          <img src={winnerMedal} alt="Winner Medal" className={styles.winnerMedal} />
          <span className={styles.winnerUsername}>{roundWinner}</span>
          <div className={styles.championContainer}>
            <span className={styles.glowDot}></span>
            <span className={styles.winnerLabel}>CHAMPION</span>
          </div>
        </div>
      )}

      {/* Final Match (large card) */}
      {finalMatch && (() => {
        const playerCount = finalMatch.players.length;
        const isMultiPlayer = playerCount > 2;
        const hasResult = finalMatch.result?.winner;
        const allEliminated = isMatchAllEliminated(finalMatch);
        const isWalkover = finalMatch.result?.result_type === 'walkover';
        const isUpcoming = finalMatch.status === 'upcoming';

        return (
          <div className={`${styles.finalMatchCard} ${isMultiPlayer ? styles.multiPlayerFinalCard : ''} ${allEliminated ? styles.eliminatedCard : ''}`} >
            <div className={`${styles.roomBadge} ${styles.roomBadgeFinal}`}>#{finalMatch.room_id}</div>
            {finalMatch.time && <div className={styles.matchTime}>{new Date(finalMatch.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>}
            {isWalkover && <div className={styles.eliminatedBanner}>WALKOVER</div>}
            {!isWalkover && allEliminated && <div className={styles.eliminatedBanner}>ALL PLAYERS ELIMINATED</div>}
            <div className={`${styles.finalMatchContent} ${isMultiPlayer ? styles.multiPlayerFinalContent : ''}`}>
              {finalMatch.players.map((player, idx) => {
                const isWinner = !allEliminated && finalMatch.result?.winner === player.username;
                const isLoser = !allEliminated && hasResult && !isWinner;
                const isEliminated = !isUpcoming && (allEliminated || player.has_appeared === false);
                return (
                  <React.Fragment key={`${finalMatch.room_id}-${idx}`}>
                    <div className={styles.finalPlayer}>
                      <div className={styles.finalAvatarWrap}>
                        <img
                          src={player.profile_image || defaultAvatar}
                          alt=""
                          // className={`${styles.finalAvatar} ${isWinner ? styles.winnerAvatar : ''} ${isLoser ? styles.loserAvatar : ''} ${isEliminated ? styles.eliminatedAvatar : ''}`}
                          className={`${styles.finalAvatar} ${isWinner ? styles.winnerAvatar : ''} ${isLoser ? styles.loserAvatar : ''} ${isEliminated ? styles.loserAvatar : ''}`}
                          onError={(e) => { e.target.onerror = null; e.target.src = defaultAvatar; }}
                        />
                        {isWinner && <img src={winnerIcon} alt="Winner" className={styles.statusIcon} />}
                        {isLoser && <img src={loserIcon} alt="Loser" className={styles.statusIcon} />}
                        {isEliminated && <img src={loserIcon} alt="Eliminated" className={styles.statusIcon} />}
                      </div>
                      <span className={styles.finalRole}>{isWinner ? 'Winner' : isLoser ? 'Loser' : ''}</span>
                      {/* <span className={`${styles.finalName} ${isWinner ? styles.winnerName : ''} ${isLoser ? styles.loserName : ''} ${isEliminated ? styles.eliminatedName : ''}`}> */}
                      <span className={`${styles.finalName} ${isWinner ? styles.winnerName : ''} ${isLoser ? styles.loserName : ''} ${isEliminated ? styles.loserName : ''}`}>
                        {isUpcoming ? player.username : truncateName(player.username, 12)}
                      </span>
                      {/* {player.phone && <span className={styles.playerPhone}>{player.phone}</span>} */}
                    </div>
                    {!isMultiPlayer && idx === 0 && playerCount > 1 && (
                      <span className={styles.vsText}>VS</span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Match Grid */}
      {otherMatches.length > 0 && (
        <div className={styles.matchGrid}>
          {otherMatches.map((match) => {
            const playerCount = match.players.length;
            const isMultiPlayer = playerCount > 2;
            const isByeMatch = playerCount === 1;
            const hasResult = match.result?.winner;
            const allEliminated = !isByeMatch && isMatchAllEliminated(match);
            const isWalkover = match.result?.result_type === 'walkover';
            const isUpcoming = match.status === 'upcoming';

            return (
              <div key={match.room_id} className={`${styles.matchCard} ${isMultiPlayer ? styles.multiPlayerCard : ''} ${allEliminated ? styles.eliminatedCard : ''}`} >
                <div className={styles.roomBadge}>#{match.room_id}</div>
                {match.time && <div className={styles.matchTime}>{new Date(match.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>}
                {/* {isWalkover && <div className={styles.eliminatedBadge}>Walkover</div>} */}
                {!isWalkover && allEliminated && <div className={styles.eliminatedBadge}>All Eliminated</div>}
                <div className={`${styles.matchContent} ${isMultiPlayer ? styles.multiPlayerContent : ''}`}>
                  {match.players.map((player, idx) => {
                    const isWinner = isByeMatch || (!allEliminated && match.result?.winner === player.username);
                    const isLoser = !isByeMatch && !allEliminated && hasResult && !isWinner;
                    const isEliminated = !isByeMatch && !isUpcoming && (allEliminated || player.has_appeared === false);
                    return (
                      <React.Fragment key={`${match.room_id}-${idx}`}>
                        <div className={styles.player}>
                          <div className={styles.avatarWrap}>
                            <img
                              src={player.profile_image || defaultAvatar}
                              alt=""
                              // className={`${styles.avatar} ${isWinner ? styles.winnerAvatar : ''} ${isLoser ? styles.loserAvatar : ''} ${isEliminated ? styles.eliminatedAvatar : ''}`}
                              className={`${styles.avatar} ${isWinner ? styles.winnerAvatar : ''} ${isLoser ? styles.loserAvatar : ''} ${isEliminated ? styles.loserAvatar : ''}`}
                              onError={(e) => { e.target.onerror = null; e.target.src = defaultAvatar; }}
                            />
                            {isWinner && <img src={winnerIcon} alt="Winner" className={styles.statusIcon} />}
                            {isLoser && <img src={loserIcon} alt="Loser" className={styles.statusIcon} />}
                            {isEliminated && <img src={loserIcon} alt="Eliminated" className={styles.statusIcon} />}
                          </div>
                          {/* <span className={`${styles.playerName} ${isWinner ? styles.winnerName : ''} ${isLoser ? styles.loserName : ''} ${isEliminated ? styles.eliminatedName : ''}`}> */}
                          <span className={`${styles.playerName} ${isWinner ? styles.winnerName : ''} ${isLoser ? styles.loserName : ''} ${isEliminated ? styles.loserName : ''}`}>
                            {isUpcoming ? player.username : truncateName(player.username)}
                          </span>
                          {/* {player.phone && <span className={styles.playerPhone}>{player.phone}</span>} */}
                        </div>
                        {!isMultiPlayer && idx === 0 && playerCount > 1 && (
                          <span className={styles.vsTextSmall}>VS</span>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {playerCount === 1 && (
                    <>
                      <span className={styles.vsTextSmall}>VS</span>
                      <div className={styles.player}>
                        <div className={styles.avatarWrap}>
                          <img src={defaultAvatar} alt="" className={`${styles.avatar} ${styles.byeAvatar}`} />
                        </div>
                        <span className={styles.playerName}>BYE</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TournamentDetails;
